import { useEffect, useState } from "react";
import { getJson, postJson } from "../api";
import { formatText, useTexts } from "../texts";
import { Notice, useToast } from "../ui/kit";
import { errorText, money, priceLine, routeName, type AiError } from "./ai";
import { FilterRow, Pill, RadioCards, Window } from "./widgets";
import styles from "./ai.module.css";

export type Candidate = {
  providerModelId: number;
  providerCode: string;
  providerName: string;
  modelName: string;
  availability: string;
  protocolSupported: boolean;
  priceIn: number | null;
  priceOut: number | null;
  priceCached?: number | null;
  priceCurrency: string | null;
};

/** API key que se puede elegir para una fila: activa y probada. */
export type ChainKey = { connectionId: number; providerCode: string; name: string; keyHint: string | null };

/** Una fila de la cadena: el modelo y la API key con la que responde. */
export type ChainLink = { providerModelId: number; connectionId: number | null; reasoningLevel?: string | null };

export type PickMode = "replace" | "add" | "embedding";

/**
 * Ventanas `cambiar-modelo`, `respaldo` y `cambiar-emb` (§6, C30). En Texto y Visión se elige en orden proveedor ·
 * API key · modelo (decisión del dueño, 2026-10-30): el mismo modelo puede ir con otra API key como respaldo. Si el
 * proveedor tiene una sola API key, ya viene elegida. «Cambiar modelo» reemplaza toda la cadena y lo dice (D8);
 * «Agregar respaldo» suma al final; Embeddings cambia solo el modelo por la vía que prepara el índice (D11). Lo que
 * Bheli no puede ejecutar se ve desactivado con su motivo.
 */
export function ModelPickWindow({
  open,
  mode,
  route,
  organizationId,
  scopeName,
  inChain,
  preselect,
  onClose,
  onDone,
}: {
  open: boolean;
  mode: PickMode;
  route: string;
  organizationId: number | null;
  scopeName: string | null;
  inChain?: ChainLink[];
  preselect?: number | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const { L, locale } = useTexts();
  const toast = useToast();
  const [candidates, setCandidates] = useState<Candidate[] | null>(null);
  const [keys, setKeys] = useState<ChainKey[]>([]);
  const [failed, setFailed] = useState<string | null>(null);
  const [provider, setProvider] = useState("");
  const [key, setKey] = useState<number | null>(null);
  const [chosen, setChosen] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [opened, setOpened] = useState(false);
  if (open !== opened) {
    setOpened(open);
    if (open) {
      setCandidates(null);
      setFailed(null);
      setError(null);
      setProvider("");
      setKey(null);
      setChosen(preselect ? String(preselect) : "");
    }
  }
  useEffect(() => {
    if (!open) return;
    let live = true;
    const query = new URLSearchParams({ route, ...(organizationId ? { organizationId: String(organizationId) } : {}) });
    void getJson<{ candidates: Candidate[]; keys: ChainKey[] } & AiError>(`/api/admin/ai/chain?${query}`).then(
      (reply) => {
        if (!live) return;
        if (reply.status === 200 && reply.body) {
          setCandidates(reply.body.candidates);
          setKeys(reply.body.keys ?? []);
        } else setFailed(errorText(L, reply.body));
      },
    );
    return () => {
      live = false;
    };
  }, [open, route, organizationId, L]);

  const routeText = routeName(L, route);
  const scopeText = scopeName ?? L("ADM_AI_GLOBAL");
  const title =
    mode === "add"
      ? formatText(L("ADM_AI_ADD_BACKUP_TITLE"), routeText)
      : formatText(L("ADM_AI_CHANGE_MODEL_TITLE"), routeText);
  const embedding = mode === "embedding";

  // Proveedores con modelos para la ruta y al menos una API key elegible, en el orden en que llegan.
  const providers = embedding
    ? []
    : [...new Map((candidates ?? []).map((item) => [item.providerCode, item.providerName])).entries()]
        .filter(([code]) => keys.some((item) => item.providerCode === code))
        .map(([code, name]) => ({ code, name }));
  const shownProvider = providers.length === 1 ? providers[0]!.code : provider;
  const providerKeys = keys.filter((item) => item.providerCode === shownProvider);
  const shownKey = providerKeys.length === 1 ? providerKeys[0]!.connectionId : key;
  const taken = (id: number) =>
    mode === "add" && (inChain ?? []).some((link) => link.providerModelId === id && link.connectionId === shownKey);

  const options = (candidates ?? [])
    .filter((item) => embedding || item.providerCode === shownProvider)
    .map((item) => {
      const blocked = !item.protocolSupported;
      const already = taken(item.providerModelId);
      // Mismo texto que las filas de Rutas (C24 1.14.11): entrada · en caché · salida por 1M, o «Sin precio». Un modelo de
      // embeddings solo se cobra por la entrada.
      const price = embedding
        ? money(locale, item.priceIn, item.priceCurrency)
        : (priceLine(
            L,
            locale,
            { input: item.priceIn, cached: item.priceCached, output: item.priceOut },
            item.priceCurrency,
          ) ?? L("ADM_AI_NO_PRICE_CAP"));
      const where = embedding ? `${item.providerName}, ` : "";
      return {
        value: String(item.providerModelId),
        title: item.modelName,
        line: blocked
          ? `${where}${L("ADM_AI_NOT_EXECUTABLE_SHORT")}, —`
          : already
            ? `${where}${L("ADM_AI_ALREADY_IN_CHAIN")}`
            : `${where}${price}`,
        disabled: blocked || already,
      };
    });
  const ready =
    !!chosen && (embedding || shownKey !== null) && !options.find((item) => item.value === chosen)?.disabled;

  const save = async () => {
    const id = Number(chosen);
    if (!id || busy || !ready) return;
    setBusy(true);
    setError(null);
    const name = candidates?.find((item) => item.providerModelId === id)?.modelName ?? "";
    const reply = embedding
      ? await postJson<{ notice?: string } & AiError>("/api/admin/ai/assignment/change", {
          route,
          organizationId,
          providerModelId: id,
        })
      : await postJson<AiError>("/api/admin/ai/chain", {
          route,
          organizationId,
          op: mode === "add" ? "add" : "use",
          providerModelId: id,
          connectionId: shownKey,
        });
    setBusy(false);
    if (reply.status !== 200) {
      setError(errorText(L, reply.body));
      return;
    }
    toast.show(
      embedding
        ? L(
            (reply.body as { notice?: string } | null)?.notice === "PREPARING"
              ? "ADM_AI_EMB_PREPARING_TOAST"
              : "ADM_AI_EMB_SAVED",
          )
        : mode === "add"
          ? L("ADM_AI_BACKUP_ADDED")
          : formatText(L("ADM_AI_ROUTE_USES"), routeText, name),
    );
    onDone();
  };

  const empty = (
    <div className="tm-empty" role="status">
      <strong>{L("ADM_AI_NO_CANDIDATES")}</strong>
      <span>{L("ADM_AI_NO_CANDIDATES_LINE")}</span>
      <a className="tm-btn secondary" href="/Ai/Providers/Index">
        {L("ADM_AI_GO_PROVIDERS")}
      </a>
    </div>
  );

  return (
    <Window
      open={open}
      wide
      title={title}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="tm-btn secondary" onClick={onClose}>
            {L("ACTION_CANCEL")}
          </button>
          <button
            type="button"
            className="tm-btn primary"
            disabled={!ready || busy}
            aria-busy={busy || undefined}
            onClick={() => void save()}
          >
            {L(mode === "add" ? "ADM_AI_ADD" : embedding ? "ADM_AI_CHANGE" : "ADM_AI_SAVE")}
          </button>
        </>
      }
    >
      <div className={styles.windowBlock}>
        {mode === "replace" ? (
          <Notice tone="warning">{formatText(L("ADM_AI_REPLACE_NOTE"), routeText, scopeText)}</Notice>
        ) : mode === "add" ? (
          <p className={styles.note}>{L("ADM_AI_BACKUP_NOTE")}</p>
        ) : (
          <Notice tone="info">{L("ADM_AI_EMB_NOTE")}</Notice>
        )}
        {error ? <Notice tone="error">{error}</Notice> : null}
        {failed ? (
          <Notice tone="error">{failed}</Notice>
        ) : !candidates ? (
          <p className={styles.note} role="status">
            {L("NPA_LOADING")}
          </p>
        ) : embedding ? (
          options.length === 0 ? (
            empty
          ) : (
            <RadioCards label={title} value={chosen} options={options} onChange={setChosen} />
          )
        ) : providers.length === 0 ? (
          empty
        ) : (
          <>
            <div className={styles.pickStep}>
              <span className={styles.pickLabel} id="pick-provider">
                {L("ADM_AI_PROVIDER")}
              </span>
              <FilterRow label={L("ADM_AI_PROVIDER")}>
                {providers.map((item) => (
                  <Pill
                    key={item.code}
                    pressed={shownProvider === item.code}
                    onClick={() => {
                      setProvider(item.code);
                      setKey(null);
                      setChosen("");
                    }}
                  >
                    {item.name}
                  </Pill>
                ))}
              </FilterRow>
            </div>
            {shownProvider ? (
              <div className={styles.pickStep}>
                <span className={styles.pickLabel}>{L("ADM_AI_API_KEY")}</span>
                <FilterRow label={L("ADM_AI_API_KEY")}>
                  {providerKeys.map((item) => (
                    <Pill
                      key={item.connectionId}
                      pressed={shownKey === item.connectionId}
                      onClick={() => setKey(item.connectionId)}
                      label={
                        item.keyHint ? `${item.name}, ${formatText(L("ADM_AI_KEY_ENDS_IN"), item.keyHint)}` : undefined
                      }
                    >
                      {item.name}
                      {item.keyHint ? <span className={styles.pillHint}>…{item.keyHint}</span> : null}
                    </Pill>
                  ))}
                </FilterRow>
              </div>
            ) : (
              <p className={styles.note}>{L("ADM_AI_PICK_PROVIDER_FIRST")}</p>
            )}
            {shownProvider && shownKey !== null ? (
              <div className={styles.pickStep}>
                <span className={styles.pickLabel}>{L("ADM_AI_MODEL")}</span>
                <RadioCards label={L("ADM_AI_MODEL")} value={chosen} options={options} onChange={setChosen} />
              </div>
            ) : shownProvider ? (
              <p className={styles.note}>{L("ADM_AI_PICK_KEY_FIRST")}</p>
            ) : null}
          </>
        )}
      </div>
    </Window>
  );
}
